import { EventEmitter, Injectable } from '@angular/core';
import { EGamesId, Game } from '@enums/common/games.enum';
import { BetService } from './bet.service';

export class BetModel {
	public numbersChecked: number[] = [];
	public extraChecked: number[] = [];
	public auxNumbersChecked: number[] = [];
	public auxExtraChecked: number[] = [];

	constructor(size: number, extraSize: number = 0) {
		// Inicializar arrays principales
		this.numbersChecked = [];
		this.extraChecked = [];

		// Inicializar arrays auxiliares con ceros
		this.auxNumbersChecked = Array(size).fill(0);
		if (extraSize > 0) {
			this.auxExtraChecked = Array(extraSize).fill(0);
		}
	}
}

const multiples = {
	1: {
		precio: 0.5,
		numeros: { 5: 44, 6: 1, 7: 7, 8: 28, 9: 84, 10: 210, 11: 462 },
	},
	2: {
		precio: 2.5,
		numeros: { 5: 35, 6: 1, 7: 7, 8: 28, 9: 84, 10: 210 },
		estrellas: { 1: 1 },
	},
	3: {
		precio: 2.5,
		numeros: { 5: 1, 6: 6, 7: 21, 8: 56, 9: 126, 10: 252 },
		estrellas: { 2: 1, 3: 3, 4: 6, 5: 10 },
	},
	4: {
		precio: 1,
		numeros: { 5: 44, 6: 1, 7: 7, 8: 28, 9: 84, 10: 210, 11: 462 },
	},
	5: {
		precio: 1.5,
		numeros: { 5: 1, 6: 6, 7: 21, 8: 56, 9: 126, 10: 252, 11: 462 },
	},
	12: {
		precio: 1,
		numeros: { 6: 1, 7: 7, 8: 28, 9: 84, 10: 210 },
		estrellas: { 1: 1, 2: 2, 3: 3, 4: 4 },
	}
};

@Injectable({
	providedIn: 'root'
})
export class GameModel {
	public name: string;
	public board: number[];
	public boardExtra: number[];
	public bets: BetModel[] = [];
	public betType: any;

	public maxBetsLimit: number;
	public minBetsLimit: number;
	public isWeekly: boolean = false;
	private defaultNumbers: number;
	private defaultExtras: number;
	private gameId: number;

	public selectedBet: number = 0;

	constructor(private betService: BetService) { }

	public setupBoard(gameId, type): void {
		this.gameId = gameId;
		var length = 0, betSize = 0, defaultBet = 1, extraLength = 0, extraSize = 0;
		this.maxBetsLimit = 8;
		this.minBetsLimit = 1;

		switch (gameId) {
			case EGamesId.BONOLOTO:
				this.name = 'Bonoloto';
				length = 49;
				betSize = 6;
				this.minBetsLimit = this.isWeekly ? 1 : 2;
				defaultBet = this.minBetsLimit;
				break;
			case EGamesId.EURODREAMS:
				this.name = 'Eurodreams';
				length = 40;
				extraLength = 5;
				betSize = 6;
				extraSize = 1
				this.maxBetsLimit = 6;
				break;
			case EGamesId.EUROMILLONES:
				this.name = 'Euromillones';
				length = 50;
				betSize = 5;
				extraLength = 12
				extraSize = 2
				this.maxBetsLimit = 5;
				break;
			case EGamesId.PRIMITIVA:
				this.name = 'Primitiva';
				length = 49;
				betSize = 6;
				extraLength = 10 // Es 10 porque va hasta el 9 pero incluye el 0
				extraSize = 1
				break;
			case EGamesId.EL_GORDO:
				this.name = 'El Gordo';
				length = 54;
				betSize = 5;
				extraLength = 10 // Es 10 porque va hasta el 9 pero incluye el 0
				extraSize = 1
				this.maxBetsLimit = 6;
				break;
			case EGamesId.LOTOTURF:
				this.name = 'Lototurf';
				length = 31;
				betSize = 6;
				extraLength = 12;
				extraSize = 1;
				this.maxBetsLimit = 6;
				break;
		}

		this.defaultNumbers = betSize;
		this.defaultExtras = extraSize;
		this.board = Array.from({ length: length }, (_, i) => i + 1);
		let j = gameId == 4 || gameId == 5 ? 0 : 1; // En Primitiva y Gordo existe el 0 en el tablero, pero en el resto no
		this.boardExtra = Array.from({ length: extraLength }, (_, i) => i + j);
		this.bets = Array.from({ length: defaultBet }, () => new BetModel(betSize, extraSize));
	}

	public changeBetType(type: any, gameId): void {
		this.bets = [];
		this.selectedBet = 0;

		if (type.combinaciones > 1) {
			this.maxBetsLimit = 1;
		}
		else {
			switch (gameId) {
				case EGamesId.BONOLOTO:
					this.maxBetsLimit = 8;
					break;
				case EGamesId.EURODREAMS:
					this.maxBetsLimit = 6;
					break;
				case EGamesId.EUROMILLONES:
					this.maxBetsLimit = 5;
					break;
				case EGamesId.PRIMITIVA:
					this.maxBetsLimit = 8;
					break;
				case EGamesId.EL_GORDO:
					this.maxBetsLimit = 6;
					break;
				case EGamesId.LOTOTURF:
					this.maxBetsLimit = 6;
					break;
			}
		}

		let extraSize = type.estrellas ? type.estrellas : 0;
		let betNumber = type.combinaciones > 1 ? 1 : (gameId == EGamesId.BONOLOTO ? (this.minBetsLimit = this.isWeekly ? 1 : 2) : 1);

		this.bets = Array.from({ length: betNumber }, () => new BetModel(type.numeros, extraSize));
		this.setBetsToService();
	}

	public toggleNumberSelection(item: number, type): void {
		if ((this.gameId === 4 || this.gameId === 5) && type === 'extra') {
			if (this.selectedBet === 0)
				this.specialNumberSelection(item)
		} else {
			this.normalNumberSelection(item, type);
		}
	}

	private specialNumberSelection(item: number): void {

		let currentBet = this.bets[this.selectedBet].extraChecked[0];

		// Si extraChecked está vacío, inicializarlo con el valor del item
		if (this.bets[this.selectedBet].extraChecked.length === 0) {
			if (this.gameId == 4) {
				for (let i = 0; i < this.bets.length; i++) {
					// Sustituimos todo el array por unicamente ese valor
					this.bets[i].extraChecked = [item]
					this.bets[i].auxExtraChecked = [item]
				}
			} else {
				this.bets[this.selectedBet].extraChecked = [item]
				this.bets[this.selectedBet].auxExtraChecked = [item]
			}

		} else if (currentBet === item) {
			if (this.gameId == 4) {
				for (let i = 0; i < this.bets.length; i++) {
					// Sustituimos todo el array por unicamente ese valor
					this.bets[i].extraChecked = []
					this.bets[i].auxExtraChecked = []
				}
			} else {
				this.bets[this.selectedBet].extraChecked = []
				this.bets[this.selectedBet].auxExtraChecked = []
			}
		} else {
			return
		}

		this.setBetsToService();

	}

	private normalNumberSelection(item: number, type): void {
		var currentBet, auxBet;
		if (type === 'extra') {
			currentBet = this.bets[this.selectedBet].extraChecked;
			auxBet = this.bets[this.selectedBet].auxExtraChecked;
		} else {
			currentBet = this.bets[this.selectedBet].numbersChecked;
			auxBet = this.bets[this.selectedBet].auxNumbersChecked;
		}

		// Si auxBet está vacío, inicializarlo con los mismos valores que currentBet
		if (!auxBet || auxBet.length === 0) {
			auxBet = [...currentBet];
			if (type === 'extra') {
				this.bets[this.selectedBet].auxExtraChecked = auxBet;
			} else {
				this.bets[this.selectedBet].auxNumbersChecked = auxBet;
			}
		}

		// Buscar el índice del item en el array auxiliar
		const itemIndex = auxBet.indexOf(item);

		if (itemIndex !== -1) {
			// Si el número ya está seleccionado, eliminarlo
			auxBet[itemIndex] = 0;
		} else {
			// Si no está seleccionado, añadirlo si hay espacio
			const zeroIndex = auxBet.indexOf(0);
			if (zeroIndex !== -1) {
				auxBet[zeroIndex] = item;
			}
		}

		// Ordenar el array auxiliar con los números al principio y los ceros al final
		const numbersChecked = auxBet.filter(n => n !== 0).sort((a, b) => a - b);
		const zeros = Array(auxBet.length - numbersChecked.length).fill(0);

		// Actualizar el array auxiliar ordenado
		auxBet.splice(0, auxBet.length, ...numbersChecked, ...zeros);

		// Limpiar el array principal y añadir solo los números seleccionados (ya ordenados)
		currentBet.length = 0;
		currentBet.push(...numbersChecked);

		this.setBetsToService();
	}

	public cleanBet(): void {
		// Limpiar el array auxiliar
		const auxBet = this.bets[this.selectedBet].auxNumbersChecked;
		if (auxBet && auxBet.length > 0) {
			auxBet.fill(0);
		}

		// Vaciar el array principal
		const currentBet = this.bets[this.selectedBet].numbersChecked;
		currentBet.length = 0;

		// Limpiar el array auxiliar
		const auxExtra = this.bets[this.selectedBet].auxExtraChecked;
		if (auxExtra && auxExtra.length > 0) {
			auxExtra.fill(0);
		}

		// Vaciar el array principal
		const currentExtra = this.bets[this.selectedBet].extraChecked;
		currentExtra.length = 0;

		this.setBetsToService();
	}

	public randomNumberSelection(): void {
		// Obtener referencias a los arrays
		const currentBet = this.bets[this.selectedBet].numbersChecked;
		let auxBet = this.bets[this.selectedBet].auxNumbersChecked;

		// Si auxBet no existe o está vacío, inicializarlo
		if (!auxBet || auxBet.length === 0) {
			auxBet = Array(currentBet.length).fill(0);
			this.bets[this.selectedBet].auxNumbersChecked = auxBet;
		} else {
			// Limpiar el array auxiliar
			auxBet.fill(0);
		}

		// Obtener números aleatorios únicos del tablero
		const availableNumbers = [...this.board];
		const betSize = auxBet.length;

		for (let i = 0; i < betSize; i++) {
			const randomIndex = Math.floor(Math.random() * availableNumbers.length);
			auxBet[i] = availableNumbers[randomIndex];
			availableNumbers.splice(randomIndex, 1);
		}

		// Ordenar el array auxiliar con los números al principio y los ceros al final
		const numbersChecked = auxBet.filter(n => n !== 0).sort((a, b) => a - b);
		const zeros = Array(auxBet.length - numbersChecked.length).fill(0);

		// Actualizar el array auxiliar ordenado
		auxBet.splice(0, auxBet.length, ...numbersChecked, ...zeros);

		// Limpiar el array principal y añadir solo los números seleccionados (ya ordenados)
		currentBet.length = 0;
		currentBet.push(...numbersChecked);

		// Manejar el extra según las condiciones
		if (this.gameId !== 4 && this.gameId !== 5) {
			// Cambiar el extra de la misma forma que los números principales
			const currentExtra = this.bets[this.selectedBet].extraChecked;
			let auxExtra = this.bets[this.selectedBet].auxExtraChecked;

			if (!auxExtra || auxExtra.length === 0) {
				auxExtra = Array(currentExtra.length).fill(0);
				this.bets[this.selectedBet].auxExtraChecked = auxExtra;
			} else {
				auxExtra.fill(0);
			}

			const availableExtras = [...this.boardExtra];
			const extraSize = auxExtra.length;

			for (let i = 0; i < extraSize; i++) {
				const randomIndex = Math.floor(Math.random() * availableExtras.length);
				auxExtra[i] = availableExtras[randomIndex];
				availableExtras.splice(randomIndex, 1);
			}

			const extrasChecked = auxExtra.filter(n => n !== 0).sort((a, b) => a - b);
			const zeros = Array(auxExtra.length - extrasChecked.length).fill(0);

			auxExtra.splice(0, auxExtra.length, ...extrasChecked, ...zeros);
			currentExtra.length = 0;
			currentExtra.push(...extrasChecked);

		} else if (this.gameId === 5) {
			const availableExtras = [...this.boardExtra];
			const randomIndex = Math.floor(Math.random() * availableExtras.length);
			this.bets[this.selectedBet].extraChecked = [randomIndex];
			this.bets[this.selectedBet].auxExtraChecked = [randomIndex];

		} else if (this.gameId === 4 && this.selectedBet === 0) {
			const availableExtras = [...this.boardExtra];
			const randomIndex = Math.floor(Math.random() * availableExtras.length);
			this.bets.forEach(element => {
				element.extraChecked = [randomIndex];
				element.auxExtraChecked = [randomIndex];
			});
		}
		this.setBetsToService();
	}

	public setSelectedBet(index: number): void {
		if (index >= 0 && index < this.bets.length) {
			this.selectedBet = index;
		}
		this.setBetsToService();
	}

	public changeBetNumber(quantity: number): void {
		const newLength = this.bets.length + quantity;

		// Verificar límites
		if (newLength < this.minBetsLimit || newLength > this.maxBetsLimit) {
			return;
		}

		if (newLength - 1 < this.selectedBet) {
			this.selectedBet = 0;
		}

		// Manejar cambios
		if (quantity > 0) {
			// Añadir nuevas instancias de BetModel
			const betSize = this.bets[0].auxNumbersChecked.length;
			const extraSize = this.bets[0].auxExtraChecked.length;
			for (let i = 0; i < quantity; i++) {

				let newBet = new BetModel(betSize, extraSize);
				if (this.gameId === 4) { // En el caso de primitiva el extra es el mismo para todos
					newBet.extraChecked = [this.bets[0].extraChecked[0]];
					newBet.auxExtraChecked = [this.bets[0].extraChecked[0]];
				}
				this.bets.push(newBet);
			}
		} else if (quantity < 0) {
			// Eliminar últimos arrays
			this.bets.splice(quantity);
		}
		this.setBetsToService();
	}

	public getMultiplesList(gameId): any {
		if (!multiples[gameId]) return [];

		const result = [];

		if (gameId == 3 || gameId == 2 || gameId == 12) {
			const numCombinations = multiples[gameId].numeros;
			const starCombinations = multiples[gameId].estrellas;

			for (const [numIndex, numCount] of Object.entries(numCombinations)) {
				for (const [starIndex, starCount] of Object.entries(starCombinations)) {
					result.push({
						numeros: parseInt(numIndex),
						estrellas: parseInt(starIndex),
						combinaciones: Number(numCount) * Number(starCount),
						precio: multiples[gameId].precio
					});
				}
			}
		}
		else {
			const combinaciones = multiples[gameId].numeros;

			for (const [index, count] of Object.entries(combinaciones)) {
				result.push({
					numeros: parseInt(index),
					combinaciones: count,
					precio: multiples[gameId].precio
				});
			}
		}


		result.sort((a, b) => a.combinaciones - b.combinaciones);
		return result;
	}

	private setBetsToService() {
		// Crear un array auxiliar para filtrar las bets
		const auxBets = this.bets.map(bet => {
			// Usar los números del betType si existe, sino los del constructor
			const expectedNumbers = this.betType?.numeros || this.defaultNumbers;
			const expectedExtras = this.betType?.estrellas || this.defaultExtras;

			// Verificar que las longitudes coincidan
			if (bet.numbersChecked.length !== expectedNumbers ||
				(bet.extraChecked && bet.extraChecked.length !== expectedExtras)) {
				return null; // Marcar para eliminación
			}

			return bet;
		}).filter(bet => bet !== null); // Filtrar las bets válidas
		// Llamar al servicio con las bets filtradas

		// Si es gameId 4, cogemos el reintegro del primer bet
		if (this.gameId === 4 && auxBets[0]?.extraChecked.length > 0) {
			this.betService.setReintegro(auxBets[0].extraChecked[0]);
		}
		this.betService.setBetsRefactor(auxBets);
	}
}